<template>
	<div v-if="tourLoading" class="tour-overlay">
		<div class="tour-spinner"></div>
	</div>

	<SideBar
		v-if="showSidebar"
		:isOpen="isOpen"
		:desktop="desktop"
		@toggle="isOpen = !isOpen"
	/>
	<div
		class="page-wrap"
		:style="{
			'--sb-offset': sidebarOffset,
			background: route.name === 'Data' ? '#0b0e12' : undefined,
			overflow: route.name === 'LiveMaps' ? 'hidden' : undefined,
			height: route.name === 'LiveMaps' ? '100dvh' : undefined,
		}"
	>
		<router-view :key="route.fullPath" />
	</div>
	<AiPrompt
		v-if="showAiPrompt"
		:key="route.fullPath"
		:userName="currentUserName"
	/>

	<!-- tour globale -->
	<v-tour name="globalTour" :steps="currentSteps" :callbacks="tourCallbacks" />
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import SideBar from "@/components/SideBar.vue";
import {
	computed,
	inject,
	nextTick,
	onBeforeUnmount,
	onMounted,
	provide,
	ref,
	watch,
} from "vue";
import { useRoute } from "vue-router";
import AiPrompt from "./components/AiPrompt.vue";
import { authIsCached, tourMap } from "./router";

const isOpen = ref(false);
const desktop = ref(window.innerWidth >= 1024);
const route = useRoute();

/* ---- tour globale -------------------------------------------------- */
const tours = inject<Record<string, any>>("tours")!; // ricevuto da main.ts
const currentSteps = ref<any[]>([]);
const tourLoading = ref(false); // spinner on/off
// let globalSeen = false; // flag fornito dal server
const seenPages = new Set<string>(); // cache locale => evita chiamate alla stessa pagina

/* la sidebar si mostra solo se la rotta NON ha hideSidebar */
const showSidebar = computed(() => !route.meta.hideSidebar);
const showAiPrompt = computed(() => !route.meta.hideAiPrompt);

const currentUserName = computed(() => {
	try {
		const raw =
			localStorage.getItem("authUser") ||
			localStorage.getItem("user") ||
			localStorage.getItem("profile") ||
			"";
		if (raw) {
			const u = JSON.parse(raw);
			return (
				u?.username ||
				u?.name ||
				(u?.first_name && u?.last_name
					? `${u.first_name} ${u.last_name}`
					: null) ||
				(u?.email ? String(u.email).split("@")[0] : "") ||
				""
			);
		}
	} catch {}
	return "";
});

onMounted(() =>
	window.addEventListener(
		"resize",
		() => (desktop.value = window.innerWidth >= 1024),
	),
);

watch(
	() => route.fullPath,
	() => {
		isOpen.value = false;
	},
);

const sidebarOffset = computed(() => {
	if (!showSidebar.value) return "0px";
	if (desktop.value) return isOpen.value ? "220px" : "60px";
	if (!desktop.value && window.innerWidth >= 768)
		return isOpen.value ? "220px" : "0px";
	return "0px";
});
window.addEventListener(
	"resize",
	() => (desktop.value = window.innerWidth >= 1024),
);

// --- helper: filtra step visibili della pagina corrente
function visiblePageSteps(name: string) {
	const raw = tourMap[name] ?? [];
	return raw.filter((s: any) => !!document.querySelector(s.target));
}

// --- computed per abilitare il bottone in sidebar
const canStartPageTour = computed(() => {
	const name = String(route.name || "");
	return visiblePageSteps(name).length > 0;
});

// --- avvio MANUALE della tour di pagina; ritorna true se parte
async function startPageTour(): Promise<boolean> {
	const name = String(route.name || "");
	if (!name) return false;

	const steps = visiblePageSteps(name);
	if (!steps.length) return false;

	currentSteps.value = steps;
	await nextTick();

	const tour = tours?.globalTour;
	if (!tour || typeof tour.start !== "function") return false;

	tour.start();
	return true;
}

// ——— ESPORTA alla sidebar
provide("startPageTour", startPageTour);
provide("canStartPageTour", canStartPageTour);
/* ② – POST /auth/me/tour { tour:'global', page } */
async function isPageSeen(page: string) {
	if (!authIsCached()) return true;
	if (!page) return true;
	if (seenPages.has(page)) return true;

	try {
		const { data } = await axios.get("/auth/me/tour", {
			params: { tour: "global", page },
		});

		if (data.seen) seenPages.add(page);

		return data.seen;
	} catch (e) {
		console.error("Errore nel ritrovare tour 'globale'", e);
		return true;
	}
	// globalSeen = true;
}

async function markPageSeen(page: string) {
	if (!authIsCached()) return;
	if (!page || seenPages.has(page)) return;
	try {
		await axios.post("/auth/me/tour", { tour: "global", page });
		seenPages.add(page);
	} catch (e) {
		console.error("Errore nel salvare tour 'globale'", e);
	}
}

function startWhenReady(steps: any[]) {
	const everyExists = () =>
		steps.every((s: any) => document.querySelector(s.target));

	const tryStart = () => {
		if (everyExists()) {
			currentSteps.value = steps; // ✔️  prop reattiva

			nextTick(() => {
				// attesa mini-render
				tours.globalTour.start(); // ✔️  parte senza errori
			});
		} else {
			setTimeout(tryStart, 150); // riprova
		}
	};

	tryStart();
}

/* ① – legge dal backend se il tour è già visto */
async function startGlobalTourIfNeeded(page: string) {
	if (!authIsCached() || !page) return;
	if (await isPageSeen(page)) return; // server dice “già visto”

	// routeName passato da handler “route-changed”
	const steps = tourMap[page];
	if (!steps) return;

	startWhenReady(steps);
}

const tourCallbacks = {
	onStart: () => {
		tourLoading.value = true; // overlay ON
		/* Lascia il tempo al portal di vue3-tour di montarsi,
       poi lo togli:                                   */
		nextTick(() => (tourLoading.value = false));
	},
	onFinish: () => {
		markPageSeen(route.name as string);
		// tourLoading.value = false;
	},
	onSkip: () => {
		markPageSeen(route.name as string);
		// tourLoading.value = false;
	},
};

/* ---- avvio manuale (pulsante “Guida rapida” della sidebar) ------- */
provide("startTour", () => {
	const page = route.name as string | undefined;
	if (!page) return; // niente rotta corrente? esci.

	// 1) aggiorna gli step
	currentSteps.value = tourMap[page] ?? [];

	// 2) parte subito ignorando lo "seen"
	nextTick(() => tours.globalTour.start());
});

provide("isDesktop", desktop);
provide("isSidebarOpen", isOpen);

// rende la funzione disponibile ai figli (Sidebar)
// provide("startTour", manualStartTour);

/* evento emesso dal router (afterEach) */
const handler = (e: Event) => {
	const { detail: page } = e as CustomEvent<string>;
	if (page) startGlobalTourIfNeeded(page);
};

onMounted(() => {
	/* 1. listener route-changed (già ok) */
	window.addEventListener("route-changed", handler);

	/* 2. listener resize (una sola volta) */
	window.addEventListener("resize", () => {
		desktop.value = window.innerWidth >= 1024;
	});

	/* 3. avvio tour iniziale */
	tourLoading.value = true;
	try {
		const first = route.name as string | undefined;
		if (authIsCached() && first) startGlobalTourIfNeeded(first);
	} finally {
		tourLoading.value = false;
	}
});

onBeforeUnmount(() => {
	window.removeEventListener("route-changed", handler);
	/* stesso per il resize */
	window.removeEventListener("resize", () => {
		desktop.value = window.innerWidth >= 1024;
	});
});

</script>

<style scoped>
.v-toast__container {
	z-index: 30000 !important;
}

.tour-overlay {
	position: fixed;
	inset: 0;
	z-index: 12000;
	background: rgba(0, 0, 0, 0.4);
	backdrop-filter: blur(2px);
	display: flex;
	align-items: center;
	justify-content: center;
}
.tour-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid rgba(255, 255, 255, 0.25);
	border-top-color: #fff;
	border-radius: 50%;
	animation: tour-spin 1s linear infinite;
}
@keyframes tour-spin {
	from {
		transform: rotate(0);
	}
	to {
		transform: rotate(360deg);
	}
}

.page-wrap {
	margin-left: var(--sb-offset);
	transition: margin-left 0.25s ease;
	min-height: 100vh;
}

/* Pulsante tour --------------------------------------------------- */
.tour-btn {
	position: fixed;
	bottom: 24px;
	right: 24px;
	z-index: 9999;
	padding: 10px 16px;
	font: 600 14px/1 "Inter", sans-serif;
	color: #fff;
	background: #008f8f;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	transition: background 0.2s, transform 0.15s;
}
.tour-btn:hover {
	background: #00a7a7;
}
.tour-btn:active {
	transform: scale(0.96);
}
</style>
